// ============================================
// All 50 States + DC Configuration
// Home Care Compliance Requirements by State
// CORRECTED VERSION - December 2024
// ============================================
//
// CORRECTIONS MADE FROM ORIGINAL:
// 1. WY: Changed "Chapter 8" to "Chapter 9/10" (Chapter 8 is Boarding Homes, not Home Health)
// 2. WY: Changed bondRequired from false to true (minimum $2,500 fidelity bond required)
// 3. MN: Changed "Minn. R. 4668" to "Minn. Stat. § 144A.43-144A.484" (4668 partially repealed 2013-2015)
// 4. AR: Added specific regulation citation
// 5. KS: Converted to proper K.A.R. format
// 6. MS: Added Subpart 201 specification
// 7. Various: Enhanced keyRegulations with verified details
//
// VERIFICATION SOURCES:
// - State department websites
// - Administrative code databases
// - Recent regulatory updates through December 2024
// ============================================

export interface StateRegulatory {
  code: string;
  name: string;
  regulatoryBody: string;
  regulatoryBodyAbbr: string;
  licenseTypes: string[];
  websiteUrl: string;
  contactPhone?: string;
  backgroundCheck: {
    type: 'state' | 'fbi' | 'both';
    frequency: string;
    registryChecks: string[];
  };
  training: {
    initialHours: number;
    annualHours: number;
    specialRequirements: string[];
  };
  requirements: {
    tbScreening: boolean;
    evvRequired: boolean;
    rnSupervision: boolean;
    bondRequired: boolean;
    insuranceMinimum: string;
  };
  keyRegulations: string[];
  regulatoryReferences: Record<string, string>;
}

export const ALL_STATES: StateRegulatory[] = [
  {
    code: 'AL',
    name: 'Alabama',
    regulatoryBody: 'Alabama Department of Public Health',
    regulatoryBodyAbbr: 'ADPH',
    licenseTypes: ['Home Health Agency (Medicare certified only)', 'No state license required for non-medical'],
    websiteUrl: 'https://www.alabamapublichealth.gov',
    contactPhone: '(334) 206-5175',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 2 years',
      registryChecks: ['ABI', 'FBI', 'Nurse Aide Registry', 'OIG'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'Abuse Recognition'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'NO STATE LICENSURE REQUIREMENT for home health agencies',
      'Medicare certification only for skilled services',
      'Bureau of Home and Community Services oversees certified programs',
      'Contact: Provider Services Unit (334) 206-5175',
    ],
    regulatoryReferences: {
      licensing: 'Ala. Admin. Code r. 410-2-4-.07',
      operations: 'Alabama Admin Code 420-5-12-.06',
    },
  },
  {
    code: 'AK',
    name: 'Alaska',
    regulatoryBody: 'Alaska Department of Health',
    regulatoryBodyAbbr: 'DOH',
    licenseTypes: ['Home Health Agency', 'Personal Care Agency'],
    websiteUrl: 'https://health.alaska.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 6 years',
      registryChecks: ['DPS', 'FBI', 'OIG'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Biennial license renewal',
      'Cold weather emergency procedures required',
    ],
    regulatoryReferences: {
      licensing: '7 AAC 12.100-12.990',
    },
  },
  {
    code: 'AZ',
    name: 'Arizona',
    regulatoryBody: 'Arizona Department of Health Services',
    regulatoryBodyAbbr: 'ADHS',
    licenseTypes: ['Home Health Agency', 'Home Care Organization'],
    websiteUrl: 'https://www.azdhs.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 6 years',
      registryChecks: ['DPS', 'FBI', 'OIG', 'Adult Protective Services'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'Abuse Recognition'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
      'Fingerprint clearance card required',
      'Adult Protective Services registry check',
    ],
    regulatoryReferences: {
      licensing: 'A.A.C. R9-10-1201',
      backgroundScreening: 'A.R.S. § 36-411',
    },
  },
  // CORRECTED: Added specific regulation citation
  {
    code: 'AR',
    name: 'Arkansas',
    regulatoryBody: 'Arkansas Department of Health',
    regulatoryBodyAbbr: 'ADH',
    licenseTypes: ['Home Health Agency', 'Personal Care Agency'],
    websiteUrl: 'https://www.healthy.arkansas.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['ASP', 'FBI', 'OIG', 'Child Maltreatment Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Annual license renewal',
      'Child maltreatment registry check required',
      'Administrator must have healthcare management experience',
    ],
    regulatoryReferences: {
      // CORRECTED: Added specific citation
      licensing: 'Ark. Admin. Code 007.05.1 (Rules for Home Health Agencies)',
      statute: 'Ark. Code Ann. § 20-10-801 et seq.',
    },
  },
  {
    code: 'CA',
    name: 'California',
    regulatoryBody: 'California Department of Social Services / California Department of Public Health',
    regulatoryBodyAbbr: 'CDSS/CDPH',
    licenseTypes: ['Home Care Organization (HCO)', 'Home Health Agency (HHA)'],
    websiteUrl: 'https://www.cdss.ca.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'One-time with continuous monitoring',
      registryChecks: ['DOJ', 'FBI', 'Home Care Aide Registry', 'OIG'],
    },
    training: {
      initialHours: 5, // HCO: 5 hours, HHA: 120 hours
      annualHours: 5,
      specialRequirements: ['All aides must register with Home Care Aide Registry', 'HHA requires 120 hours including 20 clinical'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Two license types: HCO (CDSS) and HHA (CDPH)',
      'All aides must be W-2 employees',
      'HCO license valid for 2 years',
      '$25/hr healthcare minimum wage phasing in by 2028',
      '5 days paid sick leave required',
    ],
    regulatoryReferences: {
      hcoLicensing: 'Health & Safety Code §1796.10',
      hhaLicensing: 'Health & Safety Code §1725',
      training: '22 CCR §74650',
    },
  },
  {
    code: 'CO',
    name: 'Colorado',
    regulatoryBody: 'Colorado Department of Public Health and Environment',
    regulatoryBodyAbbr: 'CDPHE',
    licenseTypes: ['Home Care Agency', 'Home Health Agency'],
    websiteUrl: 'https://cdphe.colorado.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['CBI', 'FBI', 'OIG', 'CAPS Registry'],
    },
    training: {
      initialHours: 60,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
      'CAPS (Colorado Adult Protective Services) registry check',
    ],
    regulatoryReferences: {
      licensing: '6 CCR 1011-1 Chapter 26',
    },
  },
  {
    code: 'CT',
    name: 'Connecticut',
    regulatoryBody: 'Connecticut Department of Public Health / Department of Consumer Protection',
    regulatoryBodyAbbr: 'DPH/DCP',
    licenseTypes: ['Home Health Care Agency (DPH)', 'Homemaker-Companion Agency (DCP)'],
    websiteUrl: 'https://portal.ct.gov/DPH',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['DESPP', 'FBI', 'OIG', 'Abuse Registry'],
    },
    training: {
      initialHours: 75, // 75 hours for HHA, no formal requirement for HCA
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'Dementia Training'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true, // For medical HHA only
      bondRequired: true, // $10,000 surety bond for HCA
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'TWO LICENSE TYPES: Medical (DPH) and Non-Medical (DCP)',
      'Home Health Care Agency: DPH license, 75-hour training, RN supervision',
      'Homemaker-Companion Agency: DCP registration ($375), $10,000 surety bond required',
      'HCA cannot use terms like "nurse" or "healthcare" in name',
      'If DPH agency offers non-medical, must create separate HCA entity with DCP',
    ],
    regulatoryReferences: {
      medicalLicensing: 'Conn. Gen. Stat. § 19a-490',
      nonMedicalRegistration: 'Conn. Gen. Stat. § 20-655 through 20-687',
    },
  },
  {
    code: 'DE',
    name: 'Delaware',
    regulatoryBody: 'Delaware Division of Health Care Quality',
    regulatoryBodyAbbr: 'DHCQ',
    licenseTypes: ['Home Health Agency', 'Personal Assistance Services Agency'],
    websiteUrl: 'https://dhss.delaware.gov/dhss/dhcq',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['SBI', 'FBI', 'OIG', 'Adult Abuse Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Annual license renewal',
      'Adult Abuse Registry check required',
    ],
    regulatoryReferences: {
      licensing: '16 Del. Admin. Code 4406',
    },
  },
  {
    code: 'FL',
    name: 'Florida',
    regulatoryBody: 'Agency for Health Care Administration',
    regulatoryBodyAbbr: 'AHCA',
    licenseTypes: ['Home Health Agency', 'Homemaker/Companion Services'],
    websiteUrl: 'https://ahca.myflorida.com',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['FDLE', 'FBI', 'OIG', 'Abuse Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'Alzheimer\'s Training', 'HIV/AIDS Training'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: true,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Level 2 background screening required',
      'RN available 24/7 for skilled agencies',
      'EVV 85% compliance for Medicaid',
      'License renewal 60 days before expiration',
      'Surety bond required',
    ],
    regulatoryReferences: {
      licensing: '59A-8.003 F.A.C.',
      backgroundScreening: 'Chapter 435 F.S.',
      operations: '59A-8 F.A.C.',
    },
  },
  {
    code: 'GA',
    name: 'Georgia',
    regulatoryBody: 'Georgia Department of Community Health',
    regulatoryBodyAbbr: 'DCH',
    licenseTypes: ['Private Home Care Provider', 'Home Health Agency'],
    websiteUrl: 'https://dch.georgia.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every year',
      registryChecks: ['GCIC', 'FBI', 'OIG', 'Sex Offender Registry', 'Abuse Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: false,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'GCIC and FBI background check required',
      'National Sex Offender Registry check required',
      'Annual background rescreening',
    ],
    regulatoryReferences: {
      licensing: 'Chapter 290-5-23',
      backgroundScreening: 'O.C.G.A. § 31-7-350',
    },
  },
  {
    code: 'HI',
    name: 'Hawaii',
    regulatoryBody: 'Hawaii State Department of Health',
    regulatoryBodyAbbr: 'DOH',
    licenseTypes: ['Home Health Agency', 'Home Care Agency'],
    websiteUrl: 'https://health.hawaii.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 2 years',
      registryChecks: ['HCJDC', 'FBI', 'OIG', 'Adult Abuse Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Biennial license renewal',
      'TB clearance required before patient contact',
    ],
    regulatoryReferences: {
      licensing: 'HAR Chapter 11-97',
    },
  },
  {
    code: 'ID',
    name: 'Idaho',
    regulatoryBody: 'Idaho Department of Health and Welfare',
    regulatoryBodyAbbr: 'IDHW',
    licenseTypes: ['Home Health Agency (NO LICENSE REQUIRED as of 7/1/2025)', 'Residential Care Facility'],
    websiteUrl: 'https://healthandwelfare.idaho.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['ISP', 'FBI', 'OIG'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'MAJOR CHANGE: Medicare Certified HHAs NO LONGER REQUIRED to be licensed as of July 1, 2025',
      'Streamlined Facilities Licensing and Inspection Act',
      'No new HHA licenses issued after July 1, 2025',
      'Residential Care/Assisted Living still licensed under IDAPA 16.03.22',
    ],
    regulatoryReferences: {
      licensing: 'IDAPA 16.03.07 (expiring for HHA)',
      residentialCare: 'IDAPA 16.03.22',
    },
  },
  {
    code: 'IL',
    name: 'Illinois',
    regulatoryBody: 'Illinois Department of Public Health',
    regulatoryBodyAbbr: 'IDPH',
    licenseTypes: ['Home Health Agency', 'Home Services Agency'],
    websiteUrl: 'https://dph.illinois.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every year',
      registryChecks: ['ISP', 'FBI', 'OIG', 'Health Care Worker Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'Abuse/Neglect Training'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Health Care Worker Background Check Act compliance',
      'Student Authorization for new workers pending clearance',
      'Annual abuse/neglect training required',
    ],
    regulatoryReferences: {
      licensing: '77 Ill. Adm. Code 245',
      backgroundScreening: '225 ILCS 46',
    },
  },
  {
    code: 'IN',
    name: 'Indiana',
    regulatoryBody: 'Indiana State Department of Health',
    regulatoryBodyAbbr: 'ISDH',
    licenseTypes: ['Home Health Agency', 'Personal Services Agency'],
    websiteUrl: 'https://www.in.gov/health',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['ISP', 'FBI', 'OIG', 'Sex Offender Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
      'Limited criminal history check for certain offenses',
    ],
    regulatoryReferences: {
      licensing: '410 IAC 17-8',
    },
  },
  {
    code: 'IA',
    name: 'Iowa',
    regulatoryBody: 'Iowa Department of Inspections and Appeals',
    regulatoryBodyAbbr: 'DIA',
    licenseTypes: ['Home Health Agency', 'Homemaker-Home Health Aide Agency'],
    websiteUrl: 'https://dia.iowa.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 4 years',
      registryChecks: ['DCI', 'FBI', 'OIG', 'Dependent Adult Abuse Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'Dependent Adult Abuse Training'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Biennial license renewal',
      'Dependent adult abuse registry check required',
    ],
    regulatoryReferences: {
      licensing: 'IAC 481-57',
    },
  },
  // CORRECTED: Converted to proper K.A.R. format
  {
    code: 'KS',
    name: 'Kansas',
    regulatoryBody: 'Kansas Department of Health and Environment',
    regulatoryBodyAbbr: 'KDHE',
    licenseTypes: ['Skilled Home Health Services (SHHS)', 'HCBS', 'Supportive Care Services (SCS)'],
    websiteUrl: 'https://www.kdhe.ks.gov/582/Home-Health-Agencies',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 2 years',
      registryChecks: ['KBI', 'FBI', 'OIG', 'Adult Abuse Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['KDHE-approved Administrator course', 'CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      '2025 REGULATORY UPDATES PROPOSED (feedback through Oct 31, 2025)',
      'Administrator: 21+ years, 1 year supervisory experience, degree or approved course',
      'Background checks now required every 2 years',
      'Administrator must be on-site during inspections',
      'No restraints allowed by HHA',
      'July 1, 2025: All fees paid via PayIt online system',
      'Separate fee structures for SHHS, HCBS, and SCS',
    ],
    regulatoryReferences: {
      // CORRECTED: Proper K.A.R. format
      homeHealth: 'K.A.R. 28-51 (Home Health Agencies)',
      kdheChapter: 'KDHE Regulations Chapter 28-51',
      kdads: 'K.A.R. 26-41 (Aging & Disability)',
    },
  },
  {
    code: 'KY',
    name: 'Kentucky',
    regulatoryBody: 'Kentucky Cabinet for Health and Family Services',
    regulatoryBodyAbbr: 'CHFS',
    licenseTypes: ['Home Health Agency', 'Personal Services Agency'],
    websiteUrl: 'https://chfs.ky.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['KSP', 'FBI', 'OIG', 'Nurse Aide Registry', 'Adult Abuse Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 1 year',
      'Nurse aide registry verification required',
    ],
    regulatoryReferences: {
      licensing: '902 KAR 20:081',
    },
  },
  {
    code: 'LA',
    name: 'Louisiana',
    regulatoryBody: 'Louisiana Department of Health',
    regulatoryBodyAbbr: 'LDH',
    licenseTypes: ['Home Health Agency', 'Personal Care Attendant Services'],
    websiteUrl: 'https://ldh.la.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['LSP', 'FBI', 'OIG', 'Direct Service Worker Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Annual license renewal',
      'Direct Service Worker Registry check',
    ],
    regulatoryReferences: {
      licensing: 'LAC 48:I.Chapter 89',
    },
  },
  {
    code: 'ME',
    name: 'Maine',
    regulatoryBody: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    regulatoryBodyAbbr: 'DHHS/DLC',
    licenseTypes: ['Home Health Agency', 'Personal Care Agency (NEW July 2024)'],
    websiteUrl: 'https://www.maine.gov/dhhs/dlc',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['SBI', 'FBI', 'OIG', 'Adult Protective Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'NEW: Personal Care Agency license REQUIRED as of July 1, 2024',
      'PCA Rule 10-144 CMR Ch 129 adopted August 2024',
      'PCAs provide ADL assistance and household tasks (non-medical)',
      'HCSS agencies transitioning to 10-144 CMR Ch 108 (effective Sept 2025)',
      'Technical Assistance available from DLC/OADS',
      'Application fees are non-refundable',
    ],
    regulatoryReferences: {
      pcaLicensing: '10-144 CMR Chapter 129 (adopted 8/20/2024)',
      hcssLicensing: '10-144 CMR Chapter 108',
      assistedHousing: '10-144 CMR Chapter 113 (effective Sept 2025)',
    },
  },
  {
    code: 'MD',
    name: 'Maryland',
    regulatoryBody: 'Maryland Department of Health',
    regulatoryBodyAbbr: 'MDH',
    licenseTypes: ['Home Health Agency', 'Residential Service Agency'],
    websiteUrl: 'https://health.maryland.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['CJIS', 'FBI', 'OIG', 'Nurse Aide Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 3 years',
      'CJIS background check required',
    ],
    regulatoryReferences: {
      licensing: 'COMAR 10.07.10',
    },
  },
  {
    code: 'MA',
    name: 'Massachusetts',
    regulatoryBody: 'Massachusetts Department of Public Health',
    regulatoryBodyAbbr: 'DPH',
    licenseTypes: ['Home Health Agency', 'Home Care Agency'],
    websiteUrl: 'https://www.mass.gov/orgs/department-of-public-health',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['CORI', 'SORI', 'FBI', 'OIG', 'Disabled Persons Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'CORI Training'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
      'CORI (Criminal Offender Record Information) check required',
      'SORI (Sex Offender Registry Information) check required',
    ],
    regulatoryReferences: {
      licensing: '105 CMR 140.000',
    },
  },
  {
    code: 'MI',
    name: 'Michigan',
    regulatoryBody: 'Michigan Department of Licensing and Regulatory Affairs',
    regulatoryBodyAbbr: 'LARA',
    licenseTypes: ['Home Health Agency (Medicare certified)', 'NO STATE LICENSE for non-medical home care'],
    websiteUrl: 'https://www.michigan.gov/lara',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['MSP', 'FBI', 'OIG', 'Nurse Aide Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'IMPORTANT: NO STATE LICENSE REQUIRED for non-medical home care agencies',
      'Only Medicare-certified HHAs require federal certification',
      'Business license and insurance recommended',
      'Home for the Aged separately licensed under LARA',
    ],
    regulatoryReferences: {
      licensing: 'Mich. Admin. Code R 325.20101 (Medicare HHA only)',
      homeForAged: 'MCL 333.21301',
    },
  },
  // CORRECTED: Updated citation - Minn. R. 4668 partially repealed
  {
    code: 'MN',
    name: 'Minnesota',
    regulatoryBody: 'Minnesota Department of Health',
    regulatoryBodyAbbr: 'MDH',
    licenseTypes: ['Home Care Provider (Basic)', 'Comprehensive Home Care Provider'],
    websiteUrl: 'https://www.health.state.mn.us/facilities/regulation/homecare/',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['BCA', 'FBI', 'OIG', 'Maltreatment Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 8,
      specialRequirements: ['CPR', 'First Aid', 'Dementia Training'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 1 year',
      'Background study through NETStudy 2.0',
      'Maltreatment registry check required',
      'Basic vs Comprehensive license types based on services',
      'Integrated license available for HCBS designation',
    ],
    regulatoryReferences: {
      // CORRECTED: Minn. R. 4668 was partially repealed in 2013-2015
      // Home care licensing now primarily in statute
      licensing: 'Minn. Stat. § 144A.43-144A.484',
      assistedLiving: 'Minn. Stat. Chapter 144G',
      rules: 'Minn. R. 4668 (Class F licensees - portions still in effect)',
      backgroundScreening: 'Minn. Stat. § 245C',
    },
  },
  // CORRECTED: Added Subpart 201 specification
  {
    code: 'MS',
    name: 'Mississippi',
    regulatoryBody: 'Mississippi State Department of Health',
    regulatoryBodyAbbr: 'MSDH',
    licenseTypes: ['Home Health Agency', 'Personal Care Home'],
    websiteUrl: 'https://msdh.ms.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['DPS', 'FBI', 'OIG', 'Nurse Aide Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Annual license renewal',
      'Fingerprint-based criminal history check',
    ],
    regulatoryReferences: {
      // CORRECTED: Added Subpart specification
      licensing: '15 Miss. Admin. Code Pt. 16, Subpart 201 (Home Health Agencies)',
    },
  },
  {
    code: 'MO',
    name: 'Missouri',
    regulatoryBody: 'Missouri Department of Health and Senior Services',
    regulatoryBodyAbbr: 'DHSS',
    licenseTypes: ['Home Health Agency', 'In-Home Services Provider'],
    websiteUrl: 'https://health.mo.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['MSHP', 'FBI', 'OIG', 'Employee Disqualification Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
      'Employee Disqualification List (EDL) check required',
    ],
    regulatoryReferences: {
      licensing: '19 CSR 30-82',
    },
  },
  {
    code: 'MT',
    name: 'Montana',
    regulatoryBody: 'Montana Department of Public Health and Human Services',
    regulatoryBodyAbbr: 'DPHHS',
    licenseTypes: ['Home Health Agency', 'Personal Assistance Services'],
    websiteUrl: 'https://dphhs.mt.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 6 years',
      registryChecks: ['DOJ', 'FBI', 'OIG'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
    ],
    regulatoryReferences: {
      licensing: 'ARM 37.106.1401',
    },
  },
  {
    code: 'NE',
    name: 'Nebraska',
    regulatoryBody: 'Nebraska Department of Health and Human Services',
    regulatoryBodyAbbr: 'DHHS',
    licenseTypes: ['Home Health Agency', 'Homemaker-Home Health Aide'],
    websiteUrl: 'https://dhhs.ne.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['NSP', 'FBI', 'OIG', 'Adult Protective Services Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
      'Adult Protective Services Registry check',
    ],
    regulatoryReferences: {
      licensing: '175 NAC 11',
    },
  },
  {
    code: 'NV',
    name: 'Nevada',
    regulatoryBody: 'Nevada Health Authority (formerly DPBH/HCQC)',
    regulatoryBodyAbbr: 'NVHA',
    licenseTypes: ['Home Health Agency (Agency to Provide Nursing in Home)', 'Personal Care Agency'],
    websiteUrl: 'https://dpbh.nv.gov/Reg/HealthFacilities/',
    contactPhone: '(702) 486-6515',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['DPS', 'FBI', 'OIG'],
    },
    training: {
      initialHours: 8, // Personal care attendants: 8 hours/year minimum
      annualHours: 8,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'MAJOR TRANSITION July 1, 2025: HCQC moving to Nevada Health Authority (NVHA)',
      'Authorized by Senate Bill 494',
      'Apply via myhealthfacilitylicense.nv.gov',
      'Personal Care attendants: 18+, 8 hours training/year minimum',
      'Home Health Agency = "Agency to Provide Nursing in Home" in NRS',
      'Contact HCQC: (702) 486-6515',
    ],
    regulatoryReferences: {
      personalCare: 'NRS 449.0021, NAC 449.396-449.3982',
      homeHealth: 'NRS 449.0015, NAC 449.749',
    },
  },
  {
    code: 'NH',
    name: 'New Hampshire',
    regulatoryBody: 'New Hampshire Department of Health and Human Services',
    regulatoryBodyAbbr: 'DHHS',
    licenseTypes: ['Home Health Agency', 'Home Care Service Provider'],
    websiteUrl: 'https://www.dhhs.nh.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['DOS', 'FBI', 'OIG', 'State Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
    ],
    regulatoryReferences: {
      licensing: 'He-P 809',
    },
  },
  {
    code: 'NJ',
    name: 'New Jersey',
    regulatoryBody: 'New Jersey Department of Health',
    regulatoryBodyAbbr: 'NJDOH',
    licenseTypes: ['Home Health Agency', 'Homemaker-Home Health Aide Agency'],
    websiteUrl: 'https://www.nj.gov/health',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 2 years',
      registryChecks: ['SBI', 'FBI', 'OIG', 'Nurse Aide Registry'],
    },
    training: {
      initialHours: 60,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'CNA Certification for CNAs'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
      'Criminal History Record Information (CHRI) check required',
      'Separate licenses for HHA and H-HHA',
    ],
    regulatoryReferences: {
      licensing: 'N.J.A.C. 8:42',
      backgroundScreening: 'N.J.S.A. 45:11-24.3',
    },
  },
  {
    code: 'NM',
    name: 'New Mexico',
    regulatoryBody: 'New Mexico Health Care Authority - Division of Health Improvement',
    regulatoryBodyAbbr: 'NMHCA/DHI',
    licenseTypes: ['Home Health Agency', 'Personal Care Services (no license required for non-medical)'],
    websiteUrl: 'https://www.nmhealth.org',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 2 years',
      registryChecks: ['DPS', 'FBI', 'OIG', 'Employee Abuse Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'MAJOR CHANGE 7/1/2024: DOH regulation 7.28.2 NMAC REPEALED',
      'Replaced by Health Care Authority rule 8.370.22 NMAC',
      'Division of Health Improvement (DHI) now oversees licensing',
      'Non-medical personal care services DO NOT require state license',
      'Licensure fees updated 1/28/2025 under 8.370.3 NMAC',
      'Employee Abuse Registry check required',
    ],
    regulatoryReferences: {
      licensing: '8.370.22 NMAC (effective 7/1/2024)',
      fees: '8.370.3 NMAC (updated 1/28/2025)',
      caregiverScreening: '8.370.5 NMAC',
    },
  },
  {
    code: 'NY',
    name: 'New York',
    regulatoryBody: 'New York State Department of Health',
    regulatoryBodyAbbr: 'DOH',
    licenseTypes: ['Licensed Home Care Services Agency (LHCSA)', 'Certified Home Health Agency (CHHA)'],
    websiteUrl: 'https://www.health.ny.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['DCJS', 'FBI', 'OIG', 'Nurse Aide Registry', 'Sex Offender Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'Cultural Competency Training'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Certificate of Need (CON) required for LHCSA',
      'Public need methodology: fewer than 5 LHCSAs in county',
      'Annual registration via Statistical Report',
      'Cultural competency training required',
    ],
    regulatoryReferences: {
      licensing: '10 NYCRR Part 763',
      operations: '10 NYCRR Part 766',
      backgroundScreening: 'Education Law §3001-d',
    },
  },
  {
    code: 'NC',
    name: 'North Carolina',
    regulatoryBody: 'North Carolina Division of Health Service Regulation',
    regulatoryBodyAbbr: 'DHSR',
    licenseTypes: ['Home Care Agency', 'Home Health Agency'],
    websiteUrl: 'https://info.ncdhhs.gov/dhsr',
    backgroundCheck: {
      type: 'state',
      frequency: 'Every year',
      registryChecks: ['SBI', 'OIG', 'Health Care Personnel Registry'],
    },
    training: {
      initialHours: 80,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'Competency Evaluation'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'NC state criminal background check required',
      'Health Care Personnel Registry check required',
      'Competency evaluation required',
    ],
    regulatoryReferences: {
      licensing: '10A NCAC 13J',
      backgroundScreening: 'G.S. 131E-265',
    },
  },
  {
    code: 'ND',
    name: 'North Dakota',
    regulatoryBody: 'North Dakota Department of Health and Human Services',
    regulatoryBodyAbbr: 'DHHS',
    licenseTypes: ['Home Health Agency', 'In-Home Support Services'],
    websiteUrl: 'https://www.hhs.nd.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['BCI', 'FBI', 'OIG'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
    ],
    regulatoryReferences: {
      licensing: 'NDAC 33-03-24.1',
    },
  },
  {
    code: 'OH',
    name: 'Ohio',
    regulatoryBody: 'Ohio Department of Health',
    regulatoryBodyAbbr: 'ODH',
    licenseTypes: ['Home Health Agency', 'Non-Medical Home Care Agency'],
    websiteUrl: 'https://odh.ohio.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['BCI&I', 'FBI', 'OIG', 'Nurse Aide Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'BCI&I and FBI background check required',
      'Nurse aide registry verification required',
      'Supervisory visit within 14 days of admission',
    ],
    regulatoryReferences: {
      licensing: 'OAC Chapter 3701-17',
      backgroundScreening: 'ORC 3721.121',
    },
  },
  {
    code: 'OK',
    name: 'Oklahoma',
    regulatoryBody: 'Oklahoma State Department of Health',
    regulatoryBodyAbbr: 'OSDH',
    licenseTypes: ['Home Health Agency', 'Home Care Agency'],
    websiteUrl: 'https://oklahoma.gov/health.html',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every year',
      registryChecks: ['OSBI', 'FBI', 'OIG', 'Community Services Worker Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Annual license renewal',
      'Community Services Worker Registry check',
    ],
    regulatoryReferences: {
      licensing: 'OAC 310:605',
    },
  },
  {
    code: 'OR',
    name: 'Oregon',
    regulatoryBody: 'Oregon Health Authority',
    regulatoryBodyAbbr: 'OHA',
    licenseTypes: ['Home Health Agency', 'In-Home Care Agency'],
    websiteUrl: 'https://www.oregon.gov/oha',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 2 years',
      registryChecks: ['OSP', 'FBI', 'OIG'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
    ],
    regulatoryReferences: {
      licensing: 'OAR 333-536',
    },
  },
  {
    code: 'PA',
    name: 'Pennsylvania',
    regulatoryBody: 'Pennsylvania Department of Health',
    regulatoryBodyAbbr: 'DOH',
    licenseTypes: ['Home Health Agency', 'Home Care Agency'],
    websiteUrl: 'https://www.health.pa.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every year',
      registryChecks: ['PSP', 'FBI', 'OIG', 'Child Abuse History Clearance'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'Act 169 Elder Abuse Training'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'FBI fingerprint-based criminal background check required',
      'Child Abuse History Clearance required',
      'Act 169 compliance for elder abuse reporting',
      'Quarterly supervisory visits required',
    ],
    regulatoryReferences: {
      licensing: '28 Pa. Code Chapter 601',
      backgroundScreening: 'Act 73 of 2007',
    },
  },
  {
    code: 'RI',
    name: 'Rhode Island',
    regulatoryBody: 'Rhode Island Department of Health',
    regulatoryBodyAbbr: 'RIDOH',
    licenseTypes: ['Home Nursing Care Provider', 'Home Care Provider'],
    websiteUrl: 'https://health.ri.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['BCI', 'FBI', 'OIG'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
    ],
    regulatoryReferences: {
      licensing: 'R23-17-HNC',
    },
  },
  {
    code: 'SC',
    name: 'South Carolina',
    regulatoryBody: 'South Carolina Department of Public Health',
    regulatoryBodyAbbr: 'DPH',
    licenseTypes: ['Home Health Agency', 'In-Home Care Provider'],
    websiteUrl: 'https://dph.sc.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['SLED', 'FBI', 'OIG', 'Nurse Aide Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'MAJOR CHANGE 2024: DHEC restructured - now SC Dept of Public Health (DPH)',
      'Regulation 61-77 moving to new Chapter 60 (effective May 2025)',
      'License valid for 2 years',
      'SLED criminal background check required per Section 44-7-2910',
      'Title 44 Chapter 69: Licensure of Home Health Agencies',
      'Title 44 Chapter 70: Licensure of In-Home Care Providers',
    ],
    regulatoryReferences: {
      homeHealthLicensing: 'S.C. Code Ann. Regs. 61-77 (transitioning to Ch. 60)',
      inHomeCare: 'Title 44 Chapter 70',
      backgroundCheck: 'Section 44-7-2910',
    },
  },
  {
    code: 'SD',
    name: 'South Dakota',
    regulatoryBody: 'South Dakota Department of Health',
    regulatoryBodyAbbr: 'DOH',
    licenseTypes: ['Home Health Agency', 'Personal Care Services'],
    websiteUrl: 'https://doh.sd.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['DCI', 'FBI', 'OIG'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Annual license renewal',
    ],
    regulatoryReferences: {
      licensing: 'ARSD 44:73',
    },
  },
  {
    code: 'TN',
    name: 'Tennessee',
    regulatoryBody: 'Tennessee Department of Health',
    regulatoryBodyAbbr: 'TDH',
    licenseTypes: ['Home Health Agency', 'Home Care Organization'],
    websiteUrl: 'https://www.tn.gov/health.html',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['TBI', 'FBI', 'OIG', 'Vulnerable Persons Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
      'Vulnerable Persons Registry check required',
    ],
    regulatoryReferences: {
      licensing: 'Tenn. Comp. R. & Regs. 1200-08-26',
    },
  },
  {
    code: 'TX',
    name: 'Texas',
    regulatoryBody: 'Texas Health and Human Services Commission',
    regulatoryBodyAbbr: 'HHSC',
    licenseTypes: ['Home and Community Support Services Agency (HCSSA)'],
    websiteUrl: 'https://www.hhs.texas.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 2 years',
      registryChecks: ['DPS', 'FBI', 'OIG', 'Employee Misconduct Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'Workplace Violence Training'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      '3-year license term (phased in)',
      'Initial health survey within 6 months of license',
      'AlertMedia signup required by Feb 15, 2025',
      'Workplace Violence Program required (SB 463)',
      'TULIP system for applications',
    ],
    regulatoryReferences: {
      licensing: '26 TAC Chapter 558',
      operations: 'Health and Safety Code, Chapter 142',
      backgroundScreening: '26 TAC §558.260',
    },
  },
  {
    code: 'UT',
    name: 'Utah',
    regulatoryBody: 'Utah Department of Health and Human Services',
    regulatoryBodyAbbr: 'DHHS',
    licenseTypes: ['Home Health Agency', 'Personal Care Agency'],
    websiteUrl: 'https://dhhs.utah.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['BCI', 'FBI', 'OIG', 'Licensing Information System'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
      'Licensing Information System (LIS) check',
    ],
    regulatoryReferences: {
      licensing: 'Utah Admin. Code R432-700',
    },
  },
  {
    code: 'VT',
    name: 'Vermont',
    regulatoryBody: 'Vermont Department of Health',
    regulatoryBodyAbbr: 'VDH',
    licenseTypes: ['Home Health Agency', 'Personal Care Services'],
    websiteUrl: 'https://www.healthvermont.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['VCIC', 'FBI', 'OIG', 'Adult Abuse Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
      'Adult Abuse Registry check required',
    ],
    regulatoryReferences: {
      licensing: 'Vermont Admin. Code 12-5-106',
    },
  },
  {
    code: 'VA',
    name: 'Virginia',
    regulatoryBody: 'Virginia Department of Health',
    regulatoryBodyAbbr: 'VDH',
    licenseTypes: ['Home Health Organization', 'Personal Care Agency'],
    websiteUrl: 'https://www.vdh.virginia.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['VSP', 'FBI', 'OIG', 'Sex Offender Registry', 'Child Abuse Registry'],
    },
    training: {
      initialHours: 40,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 3 years',
      'Barrier crimes check required',
      'Central Registry check required',
    ],
    regulatoryReferences: {
      licensing: '12VAC5-381',
      backgroundScreening: 'Va. Code § 32.1-162.9:1',
    },
  },
  {
    code: 'WA',
    name: 'Washington',
    regulatoryBody: 'Washington State Department of Health',
    regulatoryBodyAbbr: 'DOH',
    licenseTypes: ['Home Health Agency', 'Home Care Agency', 'In-Home Services Agency'],
    websiteUrl: 'https://www.doh.wa.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['WSP', 'FBI', 'OIG', 'DSHS Background Check'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'HIV/AIDS Training'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 1 year',
      'DSHS background check system',
      'HIV/AIDS training required',
      'Plan of correction within 10 working days of deficiencies',
    ],
    regulatoryReferences: {
      licensing: 'WAC 246-335',
      statute: 'RCW 70.127',
      backgroundScreening: 'RCW 43.43.830',
    },
  },
  {
    code: 'WV',
    name: 'West Virginia',
    regulatoryBody: 'West Virginia Office of Health Facility Licensure and Certification',
    regulatoryBodyAbbr: 'OHFLAC',
    licenseTypes: ['Home Health Agency', 'Personal Care Provider'],
    websiteUrl: 'https://ohflac.wv.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 3 years',
      registryChecks: ['PSP', 'FBI', 'OIG', 'Nurse Aide Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'Annual license renewal',
    ],
    regulatoryReferences: {
      licensing: '64 CSR 13',
    },
  },
  {
    code: 'WI',
    name: 'Wisconsin',
    regulatoryBody: 'Wisconsin Department of Health Services - Division of Quality Assurance',
    regulatoryBodyAbbr: 'DHS/DQA',
    licenseTypes: ['Home Health Agency', 'Personal Care Agency'],
    websiteUrl: 'https://www.dhs.wisconsin.gov/regulations/hha',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 4 years',
      registryChecks: ['DOJ', 'FBI', 'OIG', 'Caregiver Background Check'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'NEW DHS DQA PROVIDER PORTAL launching December 2025',
      'Applications not accepted Nov 22 - Dec 14, 2025 during transition',
      'HHA license fee: $300',
      'Must be accredited by CMS-approved organization',
      'EVV required for Medicaid personal care since Jan 1, 2023',
      'Personal Care Agency governed by DHS 105.17',
    ],
    regulatoryReferences: {
      hhaLicensing: 'Wis. Admin. Code ch. DHS 133',
      pcaLicensing: 'Wis. Admin. Code § DHS 105.17',
      statute: 'Wis. Stats. § 50.49',
      backgroundScreening: 'Wis. Stat. § 50.065',
    },
  },
  // CORRECTED: Changed Chapter 8 to Chapter 9/10, added bondRequired: true
  {
    code: 'WY',
    name: 'Wyoming',
    regulatoryBody: 'Wyoming Department of Health - Healthcare Licensing and Surveys',
    regulatoryBodyAbbr: 'WDH HLS',
    licenseTypes: ['Home Health Agency', 'Personal Care Provider'],
    websiteUrl: 'https://health.wyo.gov/aging/hls/',
    contactPhone: '(307) 777-7123',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 5 years',
      registryChecks: ['DCI', 'FBI', 'OIG', 'Central Registry'],
    },
    training: {
      initialHours: 75, // CNA + 16 hours additional home health training
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid', 'CNA + 16 hours home health training for HHA'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      // CORRECTED: Was false, should be true - Minimum $2,500 fidelity bond required
      bondRequired: true,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      // CORRECTED: Added accurate details based on verified regulations
      'License period: July 1 - June 30 (annual renewal)',
      'Administrator: Bachelor\'s + 3 years health care management OR 6 years supervisory experience',
      'Supervisory Nurse: RN with 1 year home health experience (or consultation agreement)',
      'Fidelity Bond: Minimum $2,500 required, augmented by employee count',
      'HHA: CNA certification + 16 hours Board-approved home health training',
      'HHA Supervision (skilled): Every 30 days by RN or LPN',
      'HHA Care Plan Instructions: Every 60 days',
      'Homemaker Training: Minimum 8 hours before client assignment',
      'Homemaker Supervision: Every 90 days by RN/LPN',
      'Record retention: 6 years',
      'Grievance reporting: Unresolved grievances to Licensing Division within 10 days',
      'Plan of correction: 10 working days after receiving deficiencies',
      'Home Health/Hospice Hotline: 1-800-548-1367',
      'Central Registry check required for all employees (DFS: 307-777-5994)',
    ],
    regulatoryReferences: {
      // CORRECTED: Was "Chapter 8" which governs Boarding Homes/FDTC, NOT Home Health
      programAdmin: 'Chapter 9 - Program Administration of Home Health Agencies (eff. 10-15-2001)',
      licensure: 'Chapter 10 - Licensure of Home Health Agency (eff. 11-01-2001)',
      statute: 'W.S. 35-2-901 through 35-2-913 (Health Facilities Act)',
      seniorCitizens: 'W.S. 9-2-1201 et seq. (Senior Citizens Act)',
      adultProtective: 'W.S. 35-20-101 through 35-20-116 (Adult Protective Services)',
    },
  },
  {
    code: 'DC',
    name: 'District of Columbia',
    regulatoryBody: 'DC Department of Health',
    regulatoryBodyAbbr: 'DC Health',
    licenseTypes: ['Home Health Agency', 'Home Care Agency'],
    websiteUrl: 'https://dchealth.dc.gov',
    backgroundCheck: {
      type: 'both',
      frequency: 'Every 2 years',
      registryChecks: ['MPD', 'FBI', 'OIG', 'Nurse Aide Registry'],
    },
    training: {
      initialHours: 75,
      annualHours: 12,
      specialRequirements: ['CPR', 'First Aid'],
    },
    requirements: {
      tbScreening: true,
      evvRequired: true,
      rnSupervision: true,
      bondRequired: false,
      insuranceMinimum: '$1,000,000',
    },
    keyRegulations: [
      'License valid for 2 years',
    ],
    regulatoryReferences: {
      licensing: '22-B DCMR Chapter 93',
    },
  },
];

// ============================================
// Helper functions
// ============================================

export function getStateByCode(code: string): StateRegulatory | undefined {
  return ALL_STATES.find(s => s.code === code.toUpperCase());
}

export function getAllStateCodes(): string[] {
  return ALL_STATES.map(s => s.code);
}

export function getStatesByRegion(region: 'northeast' | 'southeast' | 'midwest' | 'southwest' | 'west'): StateRegulatory[] {
  const regions: Record<string, string[]> = {
    northeast: ['CT', 'DE', 'ME', 'MD', 'MA', 'NH', 'NJ', 'NY', 'PA', 'RI', 'VT', 'DC'],
    southeast: ['AL', 'AR', 'FL', 'GA', 'KY', 'LA', 'MS', 'NC', 'SC', 'TN', 'VA', 'WV'],
    midwest: ['IL', 'IN', 'IA', 'KS', 'MI', 'MN', 'MO', 'NE', 'ND', 'OH', 'SD', 'WI'],
    southwest: ['AZ', 'NM', 'OK', 'TX'],
    west: ['AK', 'CA', 'CO', 'HI', 'ID', 'MT', 'NV', 'OR', 'UT', 'WA', 'WY'],
  };
  const stateCodes = regions[region] || [];
  return ALL_STATES.filter(s => stateCodes.includes(s.code));
}

export function getStatesWithBondRequired(): StateRegulatory[] {
  return ALL_STATES.filter(s => s.requirements.bondRequired);
}

export function getStatesWithNoLicenseRequired(): StateRegulatory[] {
  return ALL_STATES.filter(s => 
    s.keyRegulations.some(r => 
      r.toLowerCase().includes('no state license') || 
      r.toLowerCase().includes('no state licensure')
    )
  );
}

export function getStatesWithRecentChanges(): StateRegulatory[] {
  return ALL_STATES.filter(s => 
    s.keyRegulations.some(r => 
      r.toLowerCase().includes('2024') || 
      r.toLowerCase().includes('2025') ||
      r.toLowerCase().includes('major change') ||
      r.toLowerCase().includes('new:')
    )
  );
}